import { SdkContext } from '../internal'
import type { ChatProfile } from './chat'
import type { NoticeSendReport } from '@/types/postAck'

/**
 * In-app notifications of the community of `X-CommunityId` (F-108 brought the
 * first types: `chat.connectionRequested`, `chat.connectionAccepted`). Live
 * through the chat socket (`notification.new|read|removed`). Unknown types are
 * ignored by the app.
 */
export interface AppNotification {
  id: string
  type: string
  actor?: ChatProfile | null
  entityType?: string | null
  entityId?: string | null
  data?: { connectionId?: string; profileId?: string; [key: string]: unknown }
  read: boolean
  readAt?: string | null
  createdAt: string
}

export interface NotificationPage {
  items: AppNotification[]
  nextCursor: string | null
  unreadCount: number
}

export class NotificationsResource {
  constructor(private ctx: SdkContext) {}

  async list(query: { cursor?: string | null; limit?: number; unread?: boolean; type?: string } = {}): Promise<NotificationPage> {
    const params: Record<string, unknown> = { limit: query.limit || 20 }
    if (query.cursor) params.cursor = query.cursor
    if (query.unread) params.unread = true
    if (query.type) params.type = query.type
    const result = await this.ctx.call<unknown>({ httpMethod: 'GET', path: '/notifications' }, { query: params })
    const page = (Array.isArray(result) ? { items: result } : result || {}) as { items?: AppNotification[]; data?: AppNotification[]; pagination?: { nextCursor?: string | null; unreadCount?: number } }
    const items = page.items || page.data || []
    return { items, nextCursor: page.pagination?.nextCursor ?? null, unreadCount: page.pagination?.unreadCount ?? items.filter((n) => !n.read).length }
  }

  async unreadCount(): Promise<number> {
    const result = await this.ctx.call<{ unreadCount?: number; count?: number } | number>({ httpMethod: 'GET', path: '/notifications/unread-count' })
    if (typeof result === 'number') return result
    return result?.unreadCount ?? result?.count ?? 0
  }

  async markRead(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/notifications/{id}/read', hasBody: true }, { id, body: {} })
  }

  async markAllRead(): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/notifications/read-all', hasBody: true }, { body: {} })
  }

  /** F-248 7: who opened one send (team notice, workflow run, read-confirmation reminder); owner, admin, moderator. */
  async sendReport(sendId: string, query: { status?: 'all' | 'read' | 'unread'; page?: number; limit?: number } = {}): Promise<NoticeSendReport> {
    const body = await this.ctx.envelope<{ data: Omit<NoticeSendReport, 'pagination'>; pagination?: NoticeSendReport['pagination'] }>(
      { httpMethod: 'GET', path: '/notifications/sends/{sendId}' },
      { sendId, query }
    )
    return { ...body.data, pagination: body.pagination }
  }

  async remove(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/notifications/{id}' }, { id })
  }
}
